import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Music4, Plus } from "lucide-react";
import { toast } from "sonner";
import { withMemberGate } from "@/lib/with-member-gate";
import { useMusicJournal } from "@/components/music-journal/useMusicJournal";
import { SongEditor } from "@/components/music-journal/SongEditor";
import { MusicalTimeline } from "@/components/music-journal/MusicalTimeline";
import { MusicSongList } from "@/components/music-journal/MusicSongList";
import { RecentSongs } from "@/components/music-journal/RecentSongs";
import { MusicCategoryManager } from "@/components/music-journal/MusicCategoryManager";
import { MusicCheckInPanel } from "@/components/music-journal/MusicCheckInPanel";
import { SafetyBanner } from "@/components/music-journal/SafetyBanner";
import { DEFAULT_CATEGORIES, type MusicSong } from "@/lib/music-journal";
import { JournalGuide } from "@/components/JournalGuide";
import { ACCENTS, accentAt } from "@/lib/journal-accents";

export const Route = createFileRoute("/music-journal")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: "Music, Memory & Healing Journal — Recovery Trauma™" },
      {
        name: "description",
        content:
          "A private Music, Memory & Healing Journal: build your Musical Life Timeline, create self-care playlists for the states you move through, and track how music settles your nervous system.",
      },
      { property: "og:title", content: "Music, Memory & Healing Journal — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Tell your life story through music, gently and privately, and notice which songs help you settle.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/music-journal" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/music-journal" }],
  }),
  component: withMemberGate(MusicJournalPage, "Music, Memory & Healing Journal"),
});

type Tab = "timeline" | "songs" | "selfcare" | "checkin";

const TABS: { key: Tab; label: string }[] = [
  { key: "timeline", label: "My Life Soundtrack" },
  { key: "songs", label: "Songs & memories" },
  { key: "selfcare", label: "Music as self-care" },
  { key: "checkin", label: "Nervous system check-in" },
];

const TAB_ACCENT: Record<Tab, ReturnType<typeof accentAt>> = {
  timeline: ACCENTS.indigo!,
  songs: ACCENTS.rose!,
  selfcare: ACCENTS.mint!,
  checkin: ACCENTS.peach!,
};

function MusicJournalPage() {
  const journal = useMusicJournal();
  const [tab, setTab] = useState<Tab>("timeline");
  const [editing, setEditing] = useState<MusicSong | null | "new">(null);

  const openSong = (song: MusicSong) => {
    setTab("songs");
    setEditing(song);
  };

  const toggleSongCategory = async (song: MusicSong, category: string) => {
    const next = song.categories.includes(category)
      ? song.categories.filter((c) => c !== category)
      : [...song.categories, category];
    await journal.saveSong({ id: song.id, categories: next });
  };

  return (
    <div className="min-h-screen pb-24">
      <div className="mx-auto max-w-4xl px-4 pt-6 sm:px-6">

        <header
          className="mt-4 rounded-[26px] border border-border/60 p-5 shadow-sm sm:p-7"
          style={{
            backgroundImage:
              "linear-gradient(150deg, var(--qa-indigo-tint) 0%, var(--qa-rose-tint) 42%, var(--qa-peach-tint) 78%, var(--card) 100%)",
          }}
        >
          <p
            className="inline-flex items-center gap-2 rounded-full bg-card px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em]"
            style={{ color: "var(--qa-indigo-ink)" }}
          >
            <Music4 className="h-3.5 w-3.5" aria-hidden /> Private · Music &amp; Memory
          </p>
          <h1 className="mt-3 font-display text-[2rem] leading-tight text-foreground sm:text-5xl">
            Your Music, Memory &amp; Healing Journal
          </h1>
          <p className="mt-3 text-[0.98rem] leading-relaxed text-foreground/80 sm:text-[1.02rem]">
            Music holds time. A few seconds of a song can put you back in a room you haven&apos;t
            stood in for twenty years. This space is for gathering those songs — the comforting
            ones, the complicated ones — and noticing what they carry for you now.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-foreground/70">
            Everything here is yours alone. It is never shared, never published and never used to
            assess or diagnose you.
          </p>
        </header>

        <div className="mt-6">
          <SafetyBanner />
        </div>
        <JournalGuide journal="music" className="mt-6" defaultOpen />

        <nav
          className="mt-6 -mx-4 flex snap-x snap-mandatory gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0"
          aria-label="Music journal sections"
        >
          {TABS.map((t) => {
            const a = TAB_ACCENT[t.key];
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => {
                  setTab(t.key);
                  setEditing(null);
                }}
                aria-current={active ? "page" : undefined}
                className={`min-h-[44px] shrink-0 snap-start whitespace-nowrap rounded-full px-4 text-sm transition ${
                  active ? "font-semibold shadow-sm" : ""
                }`}
                style={{
                  backgroundColor: a.tint,
                  color: a.ink,
                  boxShadow: active ? `inset 0 0 0 2px ${a.ink}` : `inset 0 0 0 1px ${a.tint}`,
                  opacity: active ? 1 : 0.75,
                }}
              >
                {t.label}
              </button>
            );
          })}
        </nav>

        <div className="mt-8">
          {journal.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading your journal…</p>
          ) : tab === "timeline" ? (
            <MusicalTimeline
              songs={journal.songs}
              onEdit={openSong}
              onToggleFavourite={journal.toggleFavourite}
            />
          ) : tab === "songs" ? (
            editing !== null ? (
              <SongEditor
                song={editing === "new" ? null : editing}
                categories={journal.categories}
                saving={journal.isSavingSong}
                onCancel={() => setEditing(null)}
                onSave={async (draft) => {
                  await journal.saveSong(draft);
                  toast.success("Saved — it will be here whenever you come back.");
                  setEditing(null);
                }}
              />
            ) : (
              <div className="space-y-6">
                <button
                  type="button"
                  onClick={() => setEditing("new")}
                  className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-[var(--gold-deep)] px-5 text-sm font-semibold text-white"
                >
                  <Plus className="h-4 w-4" aria-hidden /> Add a song &amp; a memory
                </button>
                <RecentSongs songs={journal.songs} onOpen={(s) => setEditing(s)} />
                <MusicSongList
                  songs={journal.songs}
                  onEdit={(s) => setEditing(s)}
                  onDelete={(s) => void journal.deleteSong(s.id)}
                  onToggleFavourite={journal.toggleFavourite}
                />
              </div>
            )
          ) : tab === "selfcare" ? (
            <MusicCategoryManager
              categories={journal.categories}
              songs={journal.songs}
              onSeed={() => void journal.seedCategories(DEFAULT_CATEGORIES)}
              onSave={journal.saveCategory}
              onDelete={(id) => void journal.deleteCategory(id)}
              onToggleSongCategory={(song, category) => void toggleSongCategory(song, category)}
            />
          ) : (
            <MusicCheckInPanel
              songs={journal.songs}
              checkins={journal.checkins}
              saving={journal.isSavingCheckIn}
              onSave={async (draft) => {
                await journal.saveCheckIn(draft);
                toast.success("Check-in saved.");
              }}
              onDelete={(id) => void journal.deleteCheckIn(id)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
